import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { RoutesWith404 } from '@monorepo/shared/componentsV2/RoutesWith404';
import { useFeatureFlags } from 'mapistry-shared/api';
import React from 'react';
import {
  Navigate,
  Route,
  useLocation,
  useParams,
} from 'react-router-dom-v5-compat';
import { LogEntriesTab } from './LogEntriesTab';
import { LogFrequencyRequirementsTab } from './LogFrequencyRequirements';
import { LogsAndViewsPage } from './LogsAndViewsPage';
import { LogsPageContent } from './LogsPage';
import { EmptyLogsSalesPage } from './LogsPage/EmptyLogsSalesPage';
import { SingleLogPage } from './SingleLogPage';
import { EditViewPage } from './Views/EditViewPage';
import { SingleViewPage } from './Views/SingleViewPage';
import { ViewDetailTab } from './Views/ViewDetailTab';
import { ViewsPageContent } from './Views/ViewsPage/ViewsPageContent';

/**
 * Views used to belong to a single Log, and were accessed via that single Log's page.
 * With Workflows, all Reports belong to a Project, and are thus nested under the Project url wise,
 * but not the specific log on that project. This component forwards all view pages from the log specific urls
 * to the corresponding project view ones (essentially just taking the `/:logId/` bit out of the path).
 */
function RedirectToPageOnViewsTab() {
  const { logId } = useParams();
  const { pathname } = useLocation();

  // going from log views to project views page, so from
  // `/:siteId/logs/:logId/views/:viewId` to
  // `/:siteId/logs/views/:viewId`
  const pathUnderProjectViews = pathname.replace(`/${logId}`, '');

  return <Navigate replace to={pathUnderProjectViews} />;
}

export function Logs() {
  const { isData2Enabled } = useFeatureFlags();

  if (!isData2Enabled) {
    return <EmptyLogsSalesPage />;
  }

  return (
    <RoutesWith404>
      <Route path="" element={<LogsAndViewsPage />}>
        <Route index element={<LogsPageContent />} />
        <Route path="views">
          <Route index element={<ViewsPageContent />} />
          <Route path="new" element={<EditViewPage />} />
          <Route path=":viewId" element={<SingleViewPage />}>
            <Route index element={<Navigate replace to="pages" />} />
            <Route path="pages">
              <Route index element={<Navigate replace to="0" />} />
              <Route path=":pageNumber">
                <Route index element={<ViewDetailTab />} />
              </Route>
            </Route>
            <Route path="edit" element={<EditViewPage />} />
          </Route>
        </Route>
      </Route>
      <Route
        path=":logId"
        element={
          <ErrorBoundary>
            <SingleLogPage />
          </ErrorBoundary>
        }
      >
        <Route index element={<Navigate replace to="records" />} />
        <Route path="records">
          <Route index element={<Navigate replace to="pages" />} />
          <Route path="pages">
            <Route index element={<Navigate replace to="0" />} />
            <Route path=":pageNumber">
              <Route index element={<LogEntriesTab />} />
            </Route>
          </Route>
        </Route>
        {/* this accounts for previous routing strategy, where views belonged to a single log */}
        <Route path="views/*" element={<RedirectToPageOnViewsTab />} />
        <Route
          path="frequencySettings"
          element={<LogFrequencyRequirementsTab />}
        />
      </Route>
    </RoutesWith404>
  );
}
